import PropTypes from 'prop-types';
import React from 'react';
import { M, colors } from '@dashboard-experience/mastodon';
import T from 'components/T';
import { i18n } from '@international/mastodon-i18n';
import Status from '../Status';
import { date, text, numberIfNumber } from '../lib/formatting';
import { isEmpty } from '../../../../lib/helpers';
import {
  ReportTable,
  ReportTableBody,
  ReportTableCell,
  ReportTableColumnHeader,
  ReportTableHead,
  ReportTableRow,
  ReportTableRowHeader,
} from 'components/Reports/Report/MastodonReport/ReportTable';

// Mapping from monolith enum contract types to i18n translation keys.
const CONTRACT_TYPE_I18N_KEYS = {
  full_time: 'components.employment.contractTypes.fullTime',
  part_time: 'components.employment.contractTypes.partTime',
  contract: 'components.employment.contractTypes.contract',
  internship: 'components.employment.contractTypes.internship',
  self_employed: 'components.employment.contractTypes.selfEmployed',
  seasonal: 'components.employment.contractTypes.seasonal',
  temporary: 'components.employment.contractTypes.temporary',
  other: 'components.employment.contractTypes.other',
};

const FIELD_NAME_I18N_KEYS = {
  name: 'components.employment.fieldNames.name',
  position: 'components.employment.fieldNames.position',
  startDate: 'components.employment.fieldNames.startDate',
  endDate: 'components.employment.fieldNames.endDate',
  contractType: 'components.employment.fieldNames.contractType',
  salary: 'components.employment.fieldNames.salary',
};

const getPresentText = () => {
  return i18n.getStr('components.employment.present');
};
const getNotAvailableText = () => {
  return i18n.getStr('components.employment.notAvailable');
};
const getNotProvidedText = () => {
  return i18n.getStr('components.employment.notProvided');
};

// What the customer's job application originally had for this field, formatted for display.
const formatCustomerProvided = (customerProvided, field) => {
  const value = customerProvided[field];
  if (field.endsWith('Date')) {
    return date(value, i18n.getStr('dateFormats.monthYearWithComma'));
  }
  if (field === 'contractType') {
    return i18n.getStr(CONTRACT_TYPE_I18N_KEYS[value]) || text(value);
  }
  return value;
};

// Amber rather than red: a discrepancy to look at, not a failure.
const ChangedBadge = () => (
  <M.Tooltip
    align='top'
    label={i18n.getStr('components.employment.changedByCandidate')}
  >
    <M.Icon
      icon='WarningAltFilled'
      size={16}
      className='employment-changed-badge'
      style={{ color: colors.uiYellow500 }}
    />
  </M.Tooltip>
);

// The job-application column, shown only when the customer pre-filled this employer.
const CustomerProvidedCell = ({ customerProvided, field }) => (
  <ReportTableCell>
    {customerProvided.candidateEditedFields?.[field] && <ChangedBadge />}
    {formatCustomerProvided(customerProvided, field) || getNotProvidedText()}
  </ReportTableCell>
);

CustomerProvidedCell.propTypes = {
  customerProvided: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
};

const EmploymentRow = ({ record, field, customerProvided, comparisonOnly }) => {
  // backend returned data
  const candidateInput = record.employer[field];
  const vendorResult = comparisonOnly ? null : record.result[field];
  const verified = vendorResult?.verified;
  const ignored = vendorResult?.ignored;

  // data to be displayed by UI
  let candidate;
  let employer;

  if (comparisonOnly) {
    // Employer name isn't independently verified, so there's nothing to show or ignore here.
    candidate = text(candidateInput);
  } else {
    const isDateField = field.endsWith('Date');
    if (isDateField) {
      candidate = date(
        candidateInput,
        i18n.getStr('dateFormats.monthYearWithComma'),
      );
      employer = vendorResult.comments;

      // endDate gets special treatment for currently employed histories
      if (field === 'endDate') {
        candidate = candidate || getPresentText();
        const employerFallback = verified
          ? getPresentText()
          : getNotAvailableText();
        employer = isEmpty(employer) ? employerFallback : employer;
      }
    } else if (field === 'position') {
      candidate = candidateInput;
      employer = vendorResult.comments;
    } else if (field === 'contractType') {
      candidate =
        i18n.getStr(CONTRACT_TYPE_I18N_KEYS[candidateInput]) ||
        text(candidateInput);
      employer = vendorResult.comments;
    } else if (field === 'salary') {
      candidate = candidateInput ? `$${numberIfNumber(candidateInput)}` : null;
      employer = vendorResult.comments
        ? `$${numberIfNumber(vendorResult.comments)}`
        : null;
    } else {
      candidate = text(candidateInput);
      employer = vendorResult.comments;
    }
  }

  const [icon, setIcon] = React.useState('');

  React.useEffect(() => {
    if (comparisonOnly) return;
    if (!ignored && !verified) {
      setIcon('WarningAltFilled');
    } else if (ignored) {
      setIcon('WarningFilled');
    }
  }, [ignored, verified, comparisonOnly]);

  const fieldNameText = i18n.getStr(FIELD_NAME_I18N_KEYS[field]) || text(field);

  return (
    <ReportTableRow>
      <ReportTableRowHeader scope='row' className='text-very-muted'>
        {fieldNameText}
      </ReportTableRowHeader>
      {/* Blank, not omitted: every row needs the same cell count, or this row's columns shift left. The prefill payload has no salary field at all, so a real cell here would be a permanent "—", implying a per-report gap that isn't one. */}
      {customerProvided &&
        (field === 'salary' ? (
          <ReportTableCell />
        ) : (
          <CustomerProvidedCell
            customerProvided={customerProvided}
            field={field}
          />
        ))}
      <ReportTableCell>{candidate || getNotAvailableText()}</ReportTableCell>
      <ReportTableCell>
        {comparisonOnly
          ? getNotProvidedText()
          : employer || getNotAvailableText()}
        {icon && <M.Icon icon={icon} className='float-right' />}
      </ReportTableCell>
    </ReportTableRow>
  );
};

EmploymentRow.propTypes = {
  record: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  customerProvided: PropTypes.object,
  comparisonOnly: PropTypes.bool,
};

EmploymentRow.defaultProps = {
  customerProvided: null,
  comparisonOnly: false,
};

const Company = ({ employer }) => {
  const {
    name,
    address: { city, state, country },
  } = employer;

  const formattedCompany = [name, city, state, country]
    .filter(Boolean)
    .join(', ');
  return <b>{formattedCompany}</b>;
};

Company.propTypes = {
  employer: PropTypes.object,
};

Company.defaultProps = {
  employer: {},
};

const Position = ({ employer }) => (
  <div>
    <b>
      <T value='components.employment.position' />
    </b>
    <br />
    {employer.position}
    <br />
    {date(
      employer.startDate,
      i18n.getStr('dateFormats.monthYearWithComma'),
    )} -{' '}
    {employer.endDate
      ? date(employer.endDate, i18n.getStr('dateFormats.monthYearWithComma'))
      : getPresentText()}
  </div>
);

Position.propTypes = {
  employer: PropTypes.object.isRequired,
};

const Manager = ({ employer }) => {
  const {
    doNotContact,
    manager: { name, position, email, phone },
  } = employer;

  const infoProvided = name || position || email || phone;

  return (
    <div>
      <b>
        <T value='components.employment.manager' />
      </b>
      <br />
      {doNotContact && (
        <span className='text-very-muted'>
          <T value='components.employment.candidateRequestedToNotContact' />
        </span>
      )}
      {!infoProvided && !doNotContact && (
        <span className='text-very-muted'>
          <T value='components.employment.noContactInfo' />
        </span>
      )}
      {infoProvided && !doNotContact && (
        <span>
          {name}, {position}
          <br />
          {email} &middot; {phone}
        </span>
      )}
    </div>
  );
};

Manager.propTypes = {
  employer: PropTypes.object,
};

Manager.defaultProps = {
  employer: {},
};

const EmploymentRecord = ({ record, customerProvided }) => {
  const employmentRecordFields = [
    'position',
    'startDate',
    'endDate',
    'contractType',
  ];
  if (
    record.result.salary.ignored === undefined ||
    record.result.salary.ignored === null
  ) {
    employmentRecordFields.push('salary');
  }
  const showCustomerColumn = Boolean(customerProvided);
  return (
    record.status !== 'canceled' && (
      <div className='employment-record'>
        <div className='d-flex'>
          <div>
            <Company employer={record.employer} />
          </div>
          <div className='ml-auto'>
            <Status status={record.status} />
          </div>
        </div>
        <hr />
        <M.Grid>
          <M.GridRow className='mb-4'>
            <M.GridCol sm={6} className='mb-2 mb-sm-0'>
              <Position employer={record.employer} />
            </M.GridCol>
            <M.GridCol sm={2} className='mb-2 mb-sm-0'>
              <Manager employer={record.employer} />
            </M.GridCol>
          </M.GridRow>
        </M.Grid>
        <ReportTable small>
          <ReportTableHead>
            <ReportTableRow>
              <ReportTableCell />
              {showCustomerColumn && (
                <ReportTableColumnHeader className='text-muted'>
                  <M.Icon icon='Document' size={16} className='mr-1' />
                  <T value='components.employment.jobApplication' />
                </ReportTableColumnHeader>
              )}
              <ReportTableColumnHeader className='text-muted'>
                {showCustomerColumn && (
                  <M.Icon icon='User' size={16} className='mr-1' />
                )}
                <T value='components.employment.candidateInput' />
              </ReportTableColumnHeader>
              <ReportTableColumnHeader className='text-muted'>
                {showCustomerColumn && (
                  <M.Icon icon='CheckmarkOutline' size={16} className='mr-1' />
                )}
                <T value='components.employment.employerResponse' />
              </ReportTableColumnHeader>
            </ReportTableRow>
          </ReportTableHead>
          <ReportTableBody>
            {showCustomerColumn && (
              <EmploymentRow
                key='row-name'
                record={record}
                field='name'
                customerProvided={customerProvided}
                comparisonOnly
              />
            )}
            {employmentRecordFields.map((field, i) => (
              <EmploymentRow
                key={`row-${i + 1}`}
                record={record}
                field={field}
                customerProvided={customerProvided}
              />
            ))}
          </ReportTableBody>
        </ReportTable>
      </div>
    )
  );
};

EmploymentRecord.propTypes = {
  record: PropTypes.object,
  customerProvided: PropTypes.object,
};

EmploymentRecord.defaultProps = {
  record: {},
  customerProvided: null,
};

export default i18n.renderTranslation()(EmploymentRecord);
